import {
  CheckCircleTwoTone,
  CrownTwoTone,
  HomeOutlined,
  InfoCircleOutlined,
  RocketOutlined,
} from '@ant-design/icons';
import { Breadcrumb, Button, message, Tooltip } from 'antd';
import Header from 'components/header';
import PageWrapper from 'components/pageWrapper';
import { useContext, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  createCheckoutSession,
  createBillingPortalSession,
} from 'services/api/users';
import DomainContext from 'services/domainContext';
import ProfileContext from 'services/profileContext';
import { PLAN, SESSION_PENDING_PRO_CHECKOUT } from 'utils/constants';
import styles from 'styles/plan.module.scss';
import {
  isRejectedValidation,
  shouldShowValidationWall,
} from 'utils/profileValidation';

const FREE_FEATURES = [
  '100% anonymous and verified backlinks',
  'Community of real B2B companies',
  'KarmaPoints earned by giving backlinks',
  '2 backlink requests at a time',
  'Backlink monitoring to ensure they remain active',
];

const KARMA_POINTS_TOOLTIP =
  'Your base KarmaPoints are tied to your DR. The 200 bonus points refresh every month so you can request more backlinks without waiting to earn them by giving.';

const PRO_FEATURES = [
  'Everything in Free',
  {
    key: 'karma-points',
    label: (
      <>
        200 KarmaPoints to make extra requests{' '}
        <Tooltip title={KARMA_POINTS_TOOLTIP}>
          <InfoCircleOutlined style={{ color: '#999', cursor: 'pointer' }} />
        </Tooltip>
      </>
    ),
  },
  'Skip the waitlist — instant access',
  '5 backlink requests at a time',
  'Access domains from all DR tiers',
  'Priority matching with high-DR domains',
  'Advanced filtering by DR, traffic and category',
];

const Check = () => (
  <CheckCircleTwoTone twoToneColor="#08a87f" className={styles.checkIcon} />
);

const Plan = () => {
  const { profile } = useContext(ProfileContext);
  const { domains } = useContext(DomainContext);
  const [billingLoading, setBillingLoading] = useState(false);

  const currentPlan = profile?.plan || PLAN.FREE;
  const isPro = currentPlan === PLAN.PRO;

  if (
    shouldShowValidationWall(profile, domains) ||
    isRejectedValidation(profile, domains)
  ) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleUpgrade = async () => {
    setBillingLoading(true);
    try {
      const { data } = await createCheckoutSession();
      sessionStorage.setItem(SESSION_PENDING_PRO_CHECKOUT, '1');
      window.location.href = data.url;
    } catch (err) {
      message.error('Could not start checkout. Please try again.');
      setBillingLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setBillingLoading(true);
    try {
      const { data } = await createBillingPortalSession();
      const url = data.url;
      const tab = window.open(url, '_blank');
      if (tab) {
        tab.opener = null;
        setBillingLoading(false);
      } else {
        message.warning(
          'Could not open a new window. Allow popups for this site, then try again.'
        );
        setBillingLoading(false);
      }
    } catch (err) {
      message.error('Could not open billing portal. Please try again.');
      setBillingLoading(false);
    }
  };

  return (
    <>
      <Header />
      <div className={styles.wrapper}>
        <PageWrapper width={900}>
          <Breadcrumb
            items={[
              {
                title: (
                  <a href="/#/dashboard">
                    <HomeOutlined />
                    &nbsp;Dashboard
                  </a>
                ),
              },
              {
                title: 'Plan',
              },
            ]}
          />
          <h1>
            <CrownTwoTone twoToneColor={'#08a87f'} />
            &nbsp;Plan
          </h1>

          <div className={styles.cards}>
            {/* Free card */}
            <div
              className={`${styles.card} ${!isPro ? styles.active : ''}`}
            >
              {!isPro && <div className={styles.currentBadge}>Current plan</div>}
              <div className={styles.priceRow}>
                <span className={styles.priceLabel}>Free</span>
              </div>
              <div className={styles.cardTitle}>
                KarmaLinks Membership
              </div>
              <div className={styles.cardDescription}>
                Access a curated list of backlink opportunities in your vertical.
                Contribute and receive backlinks in return.
              </div>
              <ul className={styles.features}>
                {FREE_FEATURES.map((f) => (
                  <li key={f} className={styles.featureItem}>
                    <Check />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Pro card */}
            <div
              className={`${styles.card} ${isPro ? styles.active : ''}`}
            >
              {isPro && <div className={styles.currentBadge}>Current plan</div>}
              <div className={styles.priceRow}>
                <span className={styles.priceLabel}>$19/month</span>
              </div>
              <div className={styles.cardTitle}>
                KarmaLinks Pro
              </div>
              <div className={styles.cardDescription}>
                Get the most out of KarmaLinks with priority access, unlimited
                requests, and advanced filters.
              </div>
              <ul className={styles.features}>
                {PRO_FEATURES.map((f) => {
                  const isObj = typeof f === 'object';
                  return (
                    <li key={isObj ? f.key : f} className={styles.featureItem}>
                      <Check />
                      <span>{isObj ? f.label : f}</span>
                    </li>
                  );
                })}
              </ul>
              {isPro ? (
                <Button
                  onClick={handleManageBilling}
                  loading={billingLoading}
                  type="primary"
                  ghost
                  block
                >
                  Manage Billing
                </Button>
              ) : (
                <Button
                  onClick={handleUpgrade}
                  loading={billingLoading}
                  type="primary"
                  icon={<RocketOutlined />}
                  block
                >
                  Upgrade to Pro
                </Button>
              )}
            </div>
          </div>
        </PageWrapper>
      </div>
    </>
  );
};

export default Plan;
